interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface CacheConfig {
  ttl?: number;
  maxSize?: number;
}

class CacheService {
  private static instance: CacheService;
  private caches = new Map<string, Map<string, CacheEntry<any>>>();
  private configs = new Map<string, CacheConfig>();

  static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  createCache(name: string, config: CacheConfig = {}) {
    this.caches.set(name, new Map());
    this.configs.set(name, {
      ttl: config.ttl || 5 * 60 * 1000, // 5 minutes default
      maxSize: config.maxSize || 1000,
    });
  }

  get<T>(cacheName: string, key: string): T | null {
    const cache = this.caches.get(cacheName);
    if (!cache) return null;

    const entry = cache.get(key);
    if (!entry) return null;

    const config = this.configs.get(cacheName)!;
    if (Date.now() - entry.timestamp > config.ttl!) {
      cache.delete(key);
      return null;
    }

    return entry.data;
  }

  set<T>(cacheName: string, key: string, data: T): void {
    let cache = this.caches.get(cacheName);
    if (!cache) {
      this.createCache(cacheName);
      cache = this.caches.get(cacheName)!;
    }

    const config = this.configs.get(cacheName)!;
    if (cache.size >= config.maxSize!) {
      const firstKey = cache.keys().next().value;
      if (firstKey) {
        cache.delete(firstKey);
      }
    }

    cache.set(key, { data, timestamp: Date.now() });
  }

  clear(cacheName: string): void {
    this.caches.get(cacheName)?.clear();
  }

  clearAll(): void {
    this.caches.forEach((cache) => cache.clear());
  }

  has(cacheName: string, key: string): boolean {
    return this.get(cacheName, key) !== null;
  }

  getStats(cacheName: string) {
    const cache = this.caches.get(cacheName);
    if (!cache) return null;

    return {
      size: cache.size,
      keys: Array.from(cache.keys()),
    };
  }
}

export const cache = CacheService.getInstance();
